import { UseFormReturn } from "react-hook-form";
import ApplicantAssetsData from "@/model/interface/ApplicantAssets";
import React from "react";
import { Pencil } from "lucide-react";

const inputClass = (hasError: boolean) =>
  `w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${hasError ? "border-red-400" : "border-gray-300"}`;

const ApplicantAssets = ({ form }: { form: UseFormReturn<ApplicantAssetsData> }) => {
  const { register, formState } = form;

  return (
    <div className="space-y-5">
      <h4 className="text-lg font-semibold text-gray-800">Assets</h4>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Residence value</h6>
        <input
          type="text"
          {...register("residence")}
          className={inputClass(!!formState.errors.residence)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.residence && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.residence.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Other property value</h6>
        <input
          type="text"
          {...register("otherProperty")}
          className={inputClass(!!formState.errors.otherProperty)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.otherProperty && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.otherProperty.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Household contents</h6>
        <input
          type="text"
          {...register("householdContents")}
          className={inputClass(!!formState.errors.householdContents)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.householdContents && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.householdContents.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Car, boats, bikes & caravans</h6>
        <input
          type="text"
          {...register("carBoatsBikesCaravans")}
          className={inputClass(!!formState.errors.carBoatsBikesCaravans)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.carBoatsBikesCaravans && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.carBoatsBikesCaravans.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Savings / offset account balance</h6>
        <input
          type="text"
          {...register("savingsOffsetAccountBalance")}
          className={inputClass(!!formState.errors.savingsOffsetAccountBalance)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.savingsOffsetAccountBalance && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.savingsOffsetAccountBalance.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Other assets</h6>
        <input
          type="text"
          {...register("otherAssets")}
          className={inputClass(!!formState.errors.otherAssets)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.otherAssets && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.otherAssets.message}</p>
        )}
      </div>

      <h4 className="text-lg font-semibold text-gray-800 pt-4">Income</h4>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Gross yearly income</h6>
        <input
          type="number"
          {...register("grossYearlyIncome", { valueAsNumber: true })}
          className={inputClass(!!formState.errors.grossYearlyIncome)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.grossYearlyIncome && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.grossYearlyIncome.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Net monthly income</h6>
        <input
          type="number"
          {...register("netMonthlyIncome", { valueAsNumber: true })}
          className={inputClass(!!formState.errors.netMonthlyIncome)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.netMonthlyIncome && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.netMonthlyIncome.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Rental income</h6>
        <input
          type="number"
          {...register("rentalIncome", { valueAsNumber: true })}
          className={inputClass(!!formState.errors.rentalIncome)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.rentalIncome && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.rentalIncome.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Parenting allowance</h6>
        <input
          type="number"
          {...register("parentingAllowance", { valueAsNumber: true })}
          className={inputClass(!!formState.errors.parentingAllowance)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.parentingAllowance && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.parentingAllowance.message}</p>
        )}
      </div>

      <div className="relative">
        <h6 className="text-sm text-gray-500 mb-1">Pension income</h6>
        <input
          type="number"
          {...register("pensionIncome", { valueAsNumber: true })}
          className={inputClass(!!formState.errors.pensionIncome)}
          placeholder="0"
        />
        <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
        {formState.errors.pensionIncome && (
          <p className="mt-1 text-sm text-red-500">{formState.errors.pensionIncome.message}</p>
        )}
      </div>
    </div>
  );
};

export default ApplicantAssets;
